// ./src/contexts/GloveboxDocumentsContext.tsx
//
// Context wrapper around the useGloveboxDocuments hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, toolbar)
//   to share the same glovebox documents state without prop-drilling.
// - Each page creates its own <GloveboxDocumentsProvider> with its own config,
//   so GloveboxPage filters don't affect DashboardPage, etc.
// - The underlying useGloveboxDocuments hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <GloveboxDocumentsProvider autoLoad={false} pageSize={20}>
//     <GloveboxPageContent />
//     <GloveboxEditDrawer />
//   </GloveboxDocumentsProvider>
//
//   // In any child component:
//   const { documents, loading, create, ... } = useGloveboxDocumentsContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalGloveboxDocumentsContext();
//   const createFn = context?.create ?? props.onCreate;
//   const loadFn = context?.get ?? props.onLoad;

import React, { createContext, useContext } from "react";

import {
  useGloveboxDocuments,
  UseGloveboxDocumentsOptions,
  UseGloveboxDocumentsReturn,
} from "@/hooks/useGloveboxDocuments";

// ============================================
// Context
// ============================================

const GloveboxDocumentsContext =
  createContext<UseGloveboxDocumentsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface GloveboxDocumentsProviderProps
  extends UseGloveboxDocumentsOptions {
  children: React.ReactNode;
}

/**
 * Wraps useGloveboxDocuments into a context provider.
 *
 * Each instance creates its own independent useGloveboxDocuments store
 * with its own filters, pagination, loading state, and error state.
 *
 * @example
 * <GloveboxDocumentsProvider autoLoad={false} pageSize={20} expiringWithinDays={30}>
 *   <GloveboxPageContent />
 * </GloveboxDocumentsProvider>
 */
export const GloveboxDocumentsProvider: React.FC<
  GloveboxDocumentsProviderProps
> = ({ children, ...options }) => {
  const gloveboxDocuments = useGloveboxDocuments(options);

  return (
    <GloveboxDocumentsContext.Provider value={gloveboxDocuments}>
      {children}
    </GloveboxDocumentsContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the GloveboxDocumentsContext. Throws if used outside a GloveboxDocumentsProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. GloveboxList, GloveboxToolbar).
 */
export const useGloveboxDocumentsContext =
  (): UseGloveboxDocumentsReturn => {
    const context = useContext(GloveboxDocumentsContext);

    if (!context) {
      throw new Error(
        "useGloveboxDocumentsContext must be used within a <GloveboxDocumentsProvider>. " +
        "Wrap your component tree with <GloveboxDocumentsProvider> or use useOptionalGloveboxDocumentsContext() instead."
      );
    }

    return context;
  };

/**
 * Access the GloveboxDocumentsContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. GloveboxEditDrawer which works both inside GloveboxPage's provider
 * and standalone on DashboardPage).
 *
 * @example
 * const context = useOptionalGloveboxDocumentsContext();
 * const createFn = context?.create ?? props.onCreate;
 * const loadFn = context?.get ?? props.onLoad;
 */
export const useOptionalGloveboxDocumentsContext =
  (): UseGloveboxDocumentsReturn | null => {
    return useContext(GloveboxDocumentsContext);
  };